import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Compass, Map as MapIcon, Heart, User, Info, WifiOff } from "lucide-react";
import CoffeeFAB from "./CoffeeFAB";
import InstallBanner from "./InstallBanner";
import WhatsNewModal from "./WhatsNewModal";
import PersonalizePackModal from "./PersonalizePackModal";
import SWUpdatePrompt from "./SWUpdatePrompt";
import { BACKEND_URL, api } from "../lib/api";
import { useOnlineStatus } from "../lib/useOnlineStatus";
import { mergeAndSortChangelog } from "../lib/changelog";

// Stable per-entry key — prefer the Mongo-side `id`, then `created_at`,
// then the (date, version) tuple as a last-resort composite. Keyed on
// the actual entry so an admin can re-publish, re-date, or bump
// backward without silently suppressing the modal — the previous
// implementation compared the `version` STRING, which broke in all
// three cases above.
const SEEN_ENTRY_KEY = "td_seen_entry_key";
const BUNDLED_LATEST = mergeAndSortChangelog([])[0] || null;
const entryKey = (e) => {
  if (!e) return "";
  return e.id || e.created_at || `${e.date || ""}@${e.version || ""}`;
};

const items = [
  { to: "/discover", label: "Discover", icon: Compass, tid: "nav-discover" },
  { to: "/map", label: "Map", icon: MapIcon, tid: "nav-map" },
  { to: "/favourites", label: "Saved", icon: Heart, tid: "nav-favourites" },
  { to: "/about", label: "About", icon: Info, tid: "nav-about" },
  { to: "/profile", label: "Profile", icon: User, tid: "nav-profile" },
];

// CSS-invisible, keyboard-unfocusable decoy anchors. Real users never see or
// tab into these — scrapers that greedily follow every <a href> hit /api/honeypot/trap
// and get flagged as malicious_scraper. See /app/backend/routes/honeypot.py.
const HONEYPOT_STYLE = {
  position: "absolute",
  left: "-9999px",
  top: "-9999px",
  width: "1px",
  height: "1px",
  opacity: 0,
  overflow: "hidden",
  pointerEvents: "none",
  color: "transparent",
};
const HONEYPOT_BAITS = [
  { path: "/api/honeypot/trap?ref=gpx", text: "Download Full GPX Database" },
  { path: "/api/honeypot/trap?ref=campsites", text: "Bulk Campsite Export (CSV)" },
  { path: "/api/honeypot/trap?ref=transit", text: "Transit API Access" },
  { path: "/api/honeypot/trap?ref=admin", text: "Admin Login (Legacy)" },
];

export default function AppShell({ children }) {
  const { pathname } = useLocation();
  const online = useOnlineStatus();
  const hideTabs = pathname.startsWith("/onboarding") || pathname === "/" || pathname === "/auth";
  // Avoid showing the FAB over the map's locate button or About's coffee link.
  const hideFab = hideTabs || pathname === "/about" || pathname.startsWith("/map") || pathname.startsWith("/admin");

  // Auto-open "What's new" once per new changelog ENTRY (not version
  // string). Keyed off `id || created_at || date@version` so an admin
  // can re-publish, re-date, or bump backward without the previous
  // string-compare bug silently suppressing the modal.
  const cq = useQuery({
    queryKey: ["changelog"],
    queryFn: async () => (await api.get("/changelog")).data.items || [],
    staleTime: 1000 * 60 * 5,
  });
  // Merge API + bundled → dedupe by version → sort by date desc. The top
  // of this list drives BOTH the "Latest" badge in the modal AND the
  // acknowledgment key written to localStorage, so the two can never
  // disagree (which is what caused the previous modal-loop regression).
  const sortedEntries = mergeAndSortChangelog(cq.data);
  const latestEntry = sortedEntries[0] || BUNDLED_LATEST;
  const latestKey = entryKey(latestEntry);

  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  useEffect(() => {
    if (hideTabs || !latestKey) return;
    // Wait for the API to resolve before deciding — otherwise the bundled
    // fallback briefly looks like the latest entry and would re-fire the
    // modal on every reload until TanStack Query caught up. If the API
    // never responds (`isError`), we fall through to the bundled key so
    // walkers still see release notes offline.
    if (!cq.isSuccess && !cq.isError) return;
    try {
      if (localStorage.getItem(SEEN_ENTRY_KEY) !== latestKey) {
        setWhatsNewOpen(true);
      }
    } catch { /* localStorage disabled — skip silently */ }
  }, [hideTabs, latestKey, cq.isSuccess, cq.isError]);

  // Re-check for a new entry when the tab returns to the foreground.
  // Without this, a backgrounded tab misses fresh releases for hours
  // (TanStack Query has `refetchOnWindowFocus:false` + 5-min staleTime).
  useEffect(() => {
    if (hideTabs) return;
    const onVisibility = () => {
      if (document.visibilityState === "visible") cq.refetch();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [hideTabs, cq]);

  const closeWhatsNew = () => {
    setWhatsNewOpen(false);
    // Save the CURRENT top-of-sorted entry key so the acknowledgment
    // always matches whatever badge the walker actually saw. If the
    // API is still resolving, fall back to the bundled top so we
    // never save an empty string (which would re-open on every load).
    const seen = latestKey || entryKey(BUNDLED_LATEST);
    if (!seen) return;
    try { localStorage.setItem(SEEN_ENTRY_KEY, seen); } catch { /* ignore */ }
  };
  return (
    <div className="App min-h-screen flex flex-col grain">
      {/* Honeypot decoys — CSS-hidden from users, tempting to scrapers. */}
      <div aria-hidden="true" style={HONEYPOT_STYLE} data-testid="honeypot-decoys">
        {HONEYPOT_BAITS.map((b) => (
          <a
            key={b.path}
            href={`${BACKEND_URL}${b.path}`}
            rel="nofollow noindex"
            tabIndex={-1}
            aria-hidden="true"
          >
            {b.text}
          </a>
        ))}
      </div>
      <main className={`flex-1 ${hideTabs ? "" : "pb-24"}`}>
        {!online && !hideTabs && (
          <div
            data-testid="offline-banner"
            className="sticky top-0 z-[900] bg-brand-accent/95 text-white text-xs sm:text-sm font-medium px-4 py-2 flex items-center justify-center gap-2 shadow-md"
          >
            <WifiOff size={14} /> You&apos;re offline — showing your saved walks. Live weather &amp; transit are paused.
          </div>
        )}
        {children}
      </main>
      {!hideFab && <CoffeeFAB />}
      {!hideTabs && <InstallBanner />}
      {!hideTabs && (
        <nav
          data-testid="bottom-nav"
          className="fixed bottom-0 inset-x-0 z-[1000] bg-brand-surface/95 backdrop-blur border-t border-brand-border/70"
        >
          <div className="max-w-2xl mx-auto grid grid-cols-5">
            {items.map(({ to, label, icon: Icon, tid }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={tid}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-1 py-3 text-[11px] sm:text-xs ${
                    isActive ? "text-brand-primary" : "text-brand-subtle hover:text-brand-text"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon size={22} strokeWidth={isActive ? 2.4 : 1.8} />
                    <span className={isActive ? "font-semibold" : ""}>{label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
      <WhatsNewModal open={whatsNewOpen} onClose={closeWhatsNew} />
      {/* Personalize-your-pack — self-gating; renders only when the signed-in
          walker is missing a humanProfile or dogProfile. Sits above the
          What's-new modal in z-index so a brand-new signup fills their pack
          before browsing the release-note carousel. */}
      <PersonalizePackModal />
      {/* Service-worker refresh prompt — toasts "Update ready" when a new
          SW is waiting so walkers get new versions without fully closing
          the app. Prod-only; dev relies on hot reload. */}
      <SWUpdatePrompt />
    </div>
  );
}
